<script setup lang="ts">
import {
  computed,
  ref,
  toRef,
  watch,
} from 'vue';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormGroup,
  FormResult,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import type {
  ISettingsNotificationConfig,
  ISettingsNotificationsForm,
  ISettingsNotificationsItem,
  ISettingsNotificationsPayload,
  ISettingsNotificationsResponse,
  ISettingsNotificationsSavePayload,
  SettingsNotificationsNamespace,
} from '@modules/general-settings/submodules/notifications/types';
import VCard from '@components/card/VCard/VCard.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import {
  TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS,
} from '@modules/general-settings/submodules/notifications/constants';
import useSettingsNotificationsSaveMutation
  from '@modules/general-settings/submodules/notifications/mutations/useSettingsNotificationsSaveMutation';
import useSettingsNotificationsListQuery
  from '@modules/general-settings/submodules/notifications/queries/useSettingsNotificationsListQuery';
import { ESettingsNotificationsNamespace } from '@modules/general-settings/submodules/notifications/types';
import { useI18n, useLanguages } from '@modules/i18n/composables';
import { useI18nStore } from '@modules/i18n/store';
import { useMerchantIdStore } from '@modules/merchant/store';
import { watchImmediate } from '@vueuse/core';

const { interfaceLanguages } = useLanguages();
const { getTranslation } = useI18n();
const { currentLocale } = useI18nStore();
const merchantId = toRef(useMerchantIdStore(), 'merchantId');

const defaultNamespace = ESettingsNotificationsNamespace.ANONYMOUS;

const formValues = ref<ISettingsNotificationsForm>({
  namespace: defaultNamespace,
  locale: currentLocale,
  header_color: '',
  text_color: '',
  body: '',
  visible: false,
});

const selectedNamespace = computed<ISettingsNotificationsPayload>(() => ({ namespace: (formValues.value?.namespace || defaultNamespace) }));

const { data: initialData, isLoading } = useSettingsNotificationsListQuery(selectedNamespace);
const { mutate: saveNotificationConfig, data: savedData, isLoading: isLoadingSubmission } = useSettingsNotificationsSaveMutation();

const notifications = ref<ISettingsNotificationsItem[]>([]);
const notificationsEmpty: ISettingsNotificationsItem[] = interfaceLanguages.value.map((a) => ({
  body: '',
  locale: a.value,
}));

const notificationBodyByLocale = computed(() => notifications.value?.find((a) => a.locale === formValues.value?.locale)?.body || '');
const findNotificationBodyByLocale = (searchLocale: string): string => notifications.value?.find((a) => a.locale === searchLocale)?.body || '';

const namespaceOptions = computed<IFormSelectInputOption<SettingsNotificationsNamespace>[]>(() => [
  {
    value: ESettingsNotificationsNamespace.ANONYMOUS,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'FOR_ANONYM').value,
  },
  {
    value: ESettingsNotificationsNamespace.AFFILIATE,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'FOR_AFFILIATE').value,
  },
]);

const formSchema = computed<FormSchema<ISettingsNotificationsForm>>(() => ({
  namespace: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'NAMESPACE').value,
    options: namespaceOptions.value,
    value: defaultNamespace,
  },
  header_color: {
    type: EFormInputTypes.COLOR,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'HEADER_COLOR').value,
    format: 'rgba',
    value: formValues.value?.header_color,
  },
  text_color: {
    type: EFormInputTypes.COLOR,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'TEXT_COLOR').value,
    format: 'hex',
    value: formValues.value?.text_color,
  },
  locale: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'LANGUAGE').value,
    options: interfaceLanguages.value,
    required: true,
    value: formValues.value?.locale,
  },
  body: {
    type: EFormInputTypes.TEXT_EDITOR,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'TEXT_CONTENT').value,
    value: formValues.value.body,
    required: true,
  },
  visible: {
    type: EFormInputTypes.CHECKBOX,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_NOTIFICATIONS, 'STATE').value,
    value: formValues.value?.visible,
  },
}));

const formGroups = computed<FormGroup<ISettingsNotificationsForm>[]>(() => ([
  {
    items: ['namespace'],
  },
  {
    items: ['header_color', 'text_color', 'locale', 'body', 'visible'],
    isLoading: isLoading.value,
    minHeight: '600px',
  },
]));

function initForm(notification: ISettingsNotificationConfig): void {
  // get empty notifications that are missing on response
  const notificationsEmptyFiltered = notificationsEmpty.filter(
    (a) => notification.text_for_notifications_attributes.findIndex((b) => a.locale === b.locale) === -1,
  );
  notifications.value = notificationsEmptyFiltered.concat(notification.text_for_notifications_attributes);

  formValues.value.text_color = notification.text_color;
  formValues.value.header_color = notification.header_color;
  formValues.value.body = notificationBodyByLocale.value;
  formValues.value.visible = notification.visible;
}

function handleDataUpdate(data: ISettingsNotificationsResponse | undefined): void {
  const notification = data?.notification;
  if (notification) {
    initForm(notification);
  }
}

watchImmediate(initialData, (newValue) => handleDataUpdate(newValue));

watch(savedData, (newValue) => handleDataUpdate(newValue));

function handleChange<
  T extends ISettingsNotificationsForm,
  K extends FormFlattenKey<T>,
>(key: K, value: FormFlattenValue<T, K> | undefined, result: FormResult<T>): void {
  const { values } = result;
  const { locale } = values;

  if (key === 'locale' && locale) {
    formValues.value.locale = locale;
    formValues.value.body = findNotificationBodyByLocale(locale);
  } else {
    formValues.value = values;
  }

  if (key === 'body') {
    const index = notifications.value.findIndex((a) => a.locale === locale);
    notifications.value[index] = { ...notifications.value[index], body: values.body };
  }
}

function handleSubmit(values: ISettingsNotificationsForm): void {
  const validBodies = notifications.value.filter((a) => a.body?.length);

  const payload: ISettingsNotificationsSavePayload = {
    notification: {
      id: initialData.value?.notification?.id || 0,
      text_color: values.text_color,
      header_color: values.header_color,
      visible: values.visible,
      text_for_notifications_attributes: validBodies,
    },
    merchant: merchantId.value,
    ...selectedNamespace.value,
  };

  saveNotificationConfig(payload);
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsNotificationsPage'">
    <VForm
      :schema="formSchema"
      :groups="formGroups"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
      :is-loading="isLoadingSubmission"
      @change="handleChange"
      @submit="handleSubmit"
    />
  </VCard>
</template>
